import React, { useEffect, useState } from "react";
import api from "../utils/api";
import { URLS } from "../utils/urls";
import {
  Cpu,
  HardDrive,
  Activity,
  Server,
  Gauge,
  Wifi,
  MonitorSmartphone,
  Clock,
  List,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { useTopLoader } from "../context/TopLoaderContext";

// You can create a utils file for these functions to keep components clean
const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const formatUptime = (seconds) => {
  const d = Math.floor(seconds / (3600 * 24));
  const h = Math.floor((seconds % (3600 * 24)) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${d}d ${h}h ${m}m`;
};

const CircleChart = ({ used, total, label, isPercentage = false }) => {
  const usedNum = Number(used) || 0;
  const totalNum = Number(total) || 1;
  const percentage = isPercentage ? usedNum : (usedNum / totalNum) * 100;
  const { start, stop } = useTopLoader();

  let usedColor;
  if (percentage <= 70) {
    usedColor = "#4ADE80"; // Green
  } else if (percentage > 70 && percentage <= 90) {
    usedColor = "#F97316"; // Orange
  } else {
    usedColor = "#EF4444"; // Red
  }

  const data = [
    { name: "Used", value: percentage },
    { name: "Free", value: 100 - percentage },
  ];

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <ResponsiveContainer width={120} height={120}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              innerRadius={40}
              outerRadius={55}
              startAngle={90}
              endAngle={-270}
              isAnimationActive={false}
            >
              <Cell fill={usedColor} />
              <Cell fill="#E5E7EB" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-gray-700">
          {percentage.toFixed(1)}%
        </div>
      </div>
      <span className="text-xs text-gray-600 mt-2">{label}</span>
    </div>
  );
};

const SystemInfo = () => {
  const [stats, setStats] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processesLoading, setProcessesLoading] = useState(true);
  const { start, stop } = useTopLoader();

  // Effect to fetch system stats
  useEffect(() => {
    const fetchStats = async () => {
      start();
      try {
        const res = await api.get(URLS.SYSTEM_STATS);
        setStats(res.data.stats);
      } catch (err) {
        console.error("Failed to fetch system stats:", err);
      } finally {
        setLoading(false);
        stop()
      }
    };
    fetchStats();
  }, []);

  // Effect to fetch running processes
  useEffect(() => {
    const fetchProcesses = async () => {
      try {
        const res = await api.get(URLS.PROCESSES);
        // Only get the top 10 processes for display
        setProcesses(res.data.processes.slice(0, 10));
      } catch (err) {
        console.error("Failed to fetch process list:", err);
      } finally {
        setProcessesLoading(false);
      }
    };

    fetchProcesses();
    // Refresh processes every 5 seconds
    const intervalId = setInterval(fetchProcesses, 5000);

    // Cleanup function to clear the interval
    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-white shadow rounded-xl">
        <p className="text-gray-500">Loading system info...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-64 bg-white shadow rounded-xl">
        <p className="text-red-500">Failed to load system info</p>
      </div>
    );
  }

  return (
    <div className="p-2">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">System Info</h2>

      {/* Circle Charts Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
        {/* CPU */}
        <div className="p-5 bg-white shadow rounded-xl flex flex-col items-center">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <Cpu size={20} /> <span className="text-sm">CPU Load</span>
          </div>
          <p className="text-lg font-bold text-[#db8654] mb-2">
            {stats.cpu.avgLoad}%
          </p>
          <CircleChart
            used={stats.cpu.avgLoad}
            total={100}
            label={`${stats.cpu.cores} Cores`}
            isPercentage
          />
        </div>

        {/* Memory */}
        <div className="p-5 bg-white shadow rounded-xl flex flex-col items-center">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <Activity size={20} /> <span className="text-sm">Memory</span>
          </div>
          <p className="text-lg font-bold text-[#db8654] mb-2">
            {formatBytes(stats.memory.used)}
          </p>
          <CircleChart
            used={stats.memory.used}
            total={stats.memory.total}
            label={`of ${formatBytes(stats.memory.total)}`}
          />
        </div>

        {/* Disk */}
        <div className="p-5 bg-white shadow rounded-xl flex flex-col items-center">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <HardDrive size={20} /> <span className="text-sm">Disk</span>
          </div>
          <p className="text-lg font-bold text-[#db8654] mb-2">
            {stats.disk[0] ? formatBytes(stats.disk[0].used) : "N/A"}
          </p>
          {stats.disk[0] && (
            <CircleChart
              used={stats.disk[0].used}
              total={stats.disk[0].size}
              label={`of ${formatBytes(stats.disk[0].size)}`}
            />
          )}
        </div>
      </div>

      {/* Detailed Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* OS Info */}
        <div className="p-6 bg-white shadow rounded-xl">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Server size={18} /> Operating System
          </h3>
          <p className="text-md font-bold text-[#db8654]">{stats.os.distro}</p>
          <p className="text-sm text-gray-500">{stats.os.release}</p>
          <p className="text-sm text-gray-500">Arch: {stats.os.arch}</p>
        </div>

        {/* CPU Details */}
        <div className="p-6 bg-white shadow rounded-xl">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Gauge size={18} /> CPU Details
          </h3>
          <p className="text-md font-bold text-[#db8654]">
            {stats.cpu.manufacturer} {stats.cpu.brand}
          </p>
          <p className="text-sm text-gray-500">Speed: {stats.cpu.speed} GHz</p>
          <p className="text-sm text-gray-500">Cores: {stats.cpu.cores}</p>
        </div>

        {/* Network Info */}
        <div className="p-6 bg-white shadow rounded-xl">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Wifi size={18} /> Network
          </h3>
          {stats.networks && stats.networks.length > 0 ? (
            stats.networks.map((net, idx) => (
              <div key={idx} className="mb-2">
                <p className="font-medium text-[#db8654]">{net.iface}</p>
                <p className="text-sm text-gray-500">IP: {net.ip4}</p>
                <p className="text-sm text-gray-500">MAC: {net.mac}</p>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">No network data</p>
          )}
        </div>

        {/* Host Info */}
        <div className="p-6 bg-white shadow rounded-xl">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MonitorSmartphone size={18} /> Host Info
          </h3>
          <p className="text-md font-bold text-[#db8654]">
            {stats.os.hostname || "Unknown"}
          </p>
          <p className="text-sm text-gray-500">Platform: {stats.os.platform}</p>
          <p className="text-sm text-gray-500">Kernel: {stats.os.kernel}</p>
          <p className="text-sm text-gray-500">
            Uptime: {formatUptime(stats.uptime)}
          </p>
        </div>
      </div>

      {/* Running Tasks Section */}
      <div className="p-6 bg-white shadow rounded-xl">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <List size={18} /> Top Running Tasks
        </h3>
        {processesLoading ? (
          <p className="text-gray-500">Loading processes...</p>
        ) : processes.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    RAM (Used)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CPU (%)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {processes.map((task) => (
                  <tr key={task.pid}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {task.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {task.pid}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatBytes(task.memory)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {task.cpu}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No running tasks found.</p>
        )}
      </div>
    </div>
  );
};

export default SystemInfo;